import { BaseProduct, ProductBuilderMachineContext } from '../types';
import { productHasVariantWithCellDesign } from './cellDesign';
import { productHasColour, productHasDayColour } from './colour';
import { isHoneycombVertical } from './isHoneycombVertical';
import { isRollerDayNight } from './isRollerDayNight';
import { isRomanBlinds } from './isRomanBlinds';
import { isVenetianBlinds } from './isVenetianBlinds';
import { productHasVariantWithLining } from './lining';
import { productHasVariantWithMaterialType } from './material';
import { productHasVariantWithMaterialTypeDayNight } from './materialTypeDayNight';

type GetDriveControls = (context: ProductBuilderMachineContext) => string[];
type GetDriveControlTag = (product: BaseProduct) => string | void;
type GetDriveControlFromDriveControlTag = (tag: string) => string;
type ProductHasDriveControl = (
  driveControl: string,
) => (product: BaseProduct) => boolean;
type ProductHasDriveControlTag = (product: BaseProduct) => boolean;

const TAG = 'Drive Control: ';

const getDriveControlsDefault: GetDriveControls = ({
  products,
  custom: {
    materialType,
    colour,
    colourDay,
    cellDesign,
    colourNight,
    dayColour,
  },
}) => {
  const colourFilter = (product: BaseProduct) => {
    return typeof colour !== 'undefined' || typeof colourDay !== 'undefined'
      ? productHasColour(colour)(product) ||
          productHasColour(colourDay)(product)
      : true;
  };

  const colourNightFilter = (product: BaseProduct) =>
    typeof colourNight !== 'undefined'
      ? productHasColour(colourNight)(product)
      : true;

  const daycolorFilter =
    typeof dayColour !== 'undefined'
      ? productHasDayColour(dayColour)
      : (product: BaseProduct) => true;

  const cellDesignFilter =
    typeof cellDesign !== 'undefined'
      ? productHasVariantWithCellDesign(cellDesign)
      : (product: BaseProduct) => true;

  const filteredProducts = products
    .filter(productHasVariantWithMaterialType(materialType))
    .filter(colourFilter)
    .filter(colourNightFilter)
    .filter(daycolorFilter)
    .filter(cellDesignFilter);

  return Array.from(
    new Set(
      filteredProducts.map(product => getDriveControlTag(product) as string),
    ),
  );
};

const getDriveControlsRollerDayNight: GetDriveControls = ({
  products,
  custom: { materialTypeDay, materialTypeNight, colourDay, colourNight },
}) => {
  const filteredProducts = products
    .filter(productHasVariantWithMaterialType(materialTypeDay))
    // .filter(productHasVariantWithMaterialType(materialTypeNight))
    .filter(productHasColour(colourDay))
    // .filter(productHasColour(colourNight))
    .filter(productHasDriveControlTag);
  return Array.from(
    new Set(
      filteredProducts.map(product => getDriveControlTag(product) as string),
    ),
  );
};

const getDriveControlsRomanBlinds: GetDriveControls = ({
  products,
  custom: { colour, lining },
}) => {
  const filteredProducts = products
    .filter(product => productHasColour(colour)(product))
    .filter(product => productHasVariantWithLining(lining)(product));

  return Array.from(
    new Set(
      filteredProducts.map(product => getDriveControlTag(product) as string),
    ),
  );
};

const getDriveControlsVenetians: GetDriveControls = ({
  products,
  custom: { colour },
}) => {
  const filteredProducts = products.filter(product =>
    productHasColour(colour)(product),
  );

  return Array.from(
    new Set(
      filteredProducts.map(product => getDriveControlTag(product) as string),
    ),
  );
};

export const getDriveControls: GetDriveControls = context => {
  if (isRollerDayNight(context)) return getDriveControlsRollerDayNight(context);
  if (isRomanBlinds(context)) return getDriveControlsRomanBlinds(context);
  if (isVenetianBlinds(context)) return getDriveControlsVenetians(context);
  return getDriveControlsDefault(context);
};

export const productHasDriveControlTag: ProductHasDriveControlTag = ({
  tags,
}) =>
  tags.some(tag => {
    return tag.includes(TAG);
  });

export const getDriveControlTag: GetDriveControlTag = ({ tags }) =>
  tags.find(tag => tag.includes(TAG));

export const getDriveControlFromDriveControlTag: GetDriveControlFromDriveControlTag = tag =>
  tag.substring(TAG.length);

export const productHasDriveControl: ProductHasDriveControl = driveControl => ({
  tags,
}) => {
  return tags.some(tag => tag === driveControl);
};
